import {Helmet} from '@dr.pogodin/react-helmet';
import {
  Button,
  Dropdown,
  DropdownItem,
  openDeleteDialog,
  showNotification,
} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray, isString} from 'ramda-adjunct';

import {useDeleteRecordAuditMutation, useListRecordAuditDefinitionQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {recordAuditRoutes} from '@dms/routes';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  handleApiError,
  Main,
  refreshDmsDatagrid,
} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

const RECORD_AUDIT_HISTORY_GRID_CODE = 'record-audit';

export function RecordAuditList() {
  const navigate = useNavigate();

  const definitionsQuery = useListRecordAuditDefinitionQuery({});

  const [deleteAudit] = useDeleteRecordAuditMutation();

  const definitions = definitionsQuery.data ?? [];

  const isNewAuditDisabled = definitions.length === 0 || definitionsQuery.isLoading;

  const actionCallback: ActionCallback = ({actionKey, rowId, deselectAll}) => {
    const auditId = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('redirectDetail', 'detail', () => {
        if (isString(auditId)) {
          navigate(composePath(recordAuditRoutes.detail, {params: {id: auditId}}));
        }
      })
      .with('delete', () => {
        if (!isString(auditId)) {
          return;
        }
        openDeleteDialog({
          onConfirm: () =>
            deleteAudit({auditId})
              .unwrap()
              .then(() =>
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'))
              )
              .then(() => refreshDmsDatagrid(RECORD_AUDIT_HISTORY_GRID_CODE, {deselectAll: true}))
              .then(deselectAll)
              .catch(handleApiError),
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <>
      <Helmet title={i18n.t('module.checklistAudits.title')} />
      <Main
        isFullHeight
        heading={i18n.t('page.checklistAudits.title')}
        actions={[
          {
            type: 'custom',
            component: (
              <Dropdown
                data-testid="recordAuditOverview-newAuditDropdown"
                dropdownControl={
                  <Button
                    data-testid="recordAuditOverview-newAudit"
                    title={i18n.t('page.checklistAudits.actions.newAudit')}
                    variant="primary"
                    isDisabled={isNewAuditDisabled}
                  />
                }
              >
                {definitions.map((definition) => (
                  <DropdownItem
                    key={definition.id}
                    data-testid={`recordAuditOverview-newAuditItem-${definition.id}`}
                    label={definition.name}
                    leftIcon="action/check_circle"
                    onClick={() => {
                      const params = new URLSearchParams({
                        recordAuditDefinitionId: definition.id,
                      });
                      navigate(`${recordAuditRoutes.create}?${params.toString()}`);
                    }}
                  />
                ))}
              </Dropdown>
            ),
          },
        ]}
      >
        <DmsDataGrid gridCode={RECORD_AUDIT_HISTORY_GRID_CODE} actionCallback={actionCallback} />
      </Main>
    </>
  );
}
